/**
 * What every app page reaches: the accounts (on this server), the theme, the current route and moving
 * to another one without reloading (`go`). Built once by `main.tsx` with {@link createPages}.
 */
import { createContext, createSignal, useContext, type Accessor } from "solid-js";
import { createAccounts, type Accounts } from "../state/accounts";
import { createTheme, type Theme } from "../state/theme";
import { pageRoute, routePath, type PageRoute } from "./routes";

/** The app pages' services. */
export interface Pages {
  /** This server (`location.origin`): where the pages sign in and the teams live. */
  server: string;
  /** The accounts service, used here only for {@link Pages.server}. */
  accounts: Accounts;
  /** Light, dark or the system's (the same choice as the editor's). */
  theme: Theme;
  /** The page shown now. */
  route: Accessor<PageRoute>;
  /** Shows another page (a new history entry; `replace` for a redirect). */
  go(route: PageRoute, replace?: boolean): void;
}

/** Starts the pages' services at the current address. Call inside a Solid root. */
export function createPages(): Pages {
  const server = location.origin;
  const [route, setRoute] = createSignal<PageRoute>(pageRoute(location.pathname) ?? { page: "home" });
  const first = route();
  const accounts = createAccounts({ homeServer: server, invite: first.page === "join" ? { server, code: first.code } : null });
  // Back and forward move between pages too.
  addEventListener("popstate", () => setRoute(pageRoute(location.pathname) ?? { page: "home" }));
  return {
    server,
    accounts,
    theme: createTheme(),
    route,
    go(next, replace = false) {
      // The query (`?debug`) stays; `next` only counts on the page it was given to.
      const params = new URLSearchParams(location.search);
      params.delete("next");
      const query = params.toString();
      const address = `${routePath(next)}${query ? `?${query}` : ""}`;
      if (replace) history.replaceState(null, "", address);
      else history.pushState(null, "", address);
      setRoute(next);
    },
  };
}

/** The context that hands {@link Pages} to every page part. */
export const PagesContext = createContext<Pages>();

/** The pages' services (inside {@link PagesContext}). */
export function usePages(): Pages {
  const pages = useContext(PagesContext);
  if (!pages) throw new Error("usePages() outside the app pages");
  return pages;
}
